import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "randmarcomps";
import { AlertTriangle, Loader2 } from "lucide-react";
import { usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSkuMutation } from "randmar-api-client";
import { inject, observer } from "mobx-react";
import React, { useState } from "react";
import WithManufacturer from "../../contexts/WithManufacturer";
import { OpportunityDetail } from "../../stores/opportunities/Opportunity";
import { Product } from '../../stores/product/Product';
import { RootStore } from "../../stores/rootStore";

interface IProps {
  store?: RootStore;
  applicationId: string;
  product: Product;
  opportunityDetail: OpportunityDetail;
  children: React.ReactNode;
}

const EditProductFromOpportunity: React.FC<IProps> = ({ store, applicationId, product, opportunityDetail, children }) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [addProductToOpportunity, { isLoading }] =
    usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSkuMutation();
  const [chosenPriceType, setChosenPriceType] = useState(opportunityDetail.bidPriceType || "ItemRebate");
  const [chosenBidPrice, setChosenBidPrice] = useState(opportunityDetail.bidPrice);

  const _handleCancel = () => {
    setOpen(false);
  };

  const _handleAddToRequisitionButton = () => {
    setOpen(true);
  };

  const handlePriceTypeChange = (value: string) => {
    setChosenPriceType(value);
  };

  const handleBidPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or a valid number format with up to 2 decimal places
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setChosenBidPrice(value === "" ? undefined : parseFloat(value));
    }
  };

  const handleConfirmProductOpportunityRequest = async () => {
    const { opportunityStore, productStore } = store!;

    try {
      await addProductToOpportunity({
        routeManufacturerId: applicationId,
        bidNumber: opportunityDetail.bidNumber || "",
        randmarSku: product.randmarSKU || "",
        bidPrice: chosenBidPrice || 0,
        bidPriceType: chosenPriceType,
      }).unwrap();

      toast({ variant: "success", title: "Success", description: "Product updated in opportunity." });

      opportunityStore.getOpportunity(applicationId, opportunityDetail.bidNumber || "", true);
      productStore.getProduct(applicationId, product.randmarSKU || "");

      setOpen(false);
    } catch (error) {
      console.error("Failed to update product in opportunity:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to update product. Please try again." });
    }
  };

  const { opportunityStore: { opportunities }, userStore } = store!;
  const isSuperAdmin = userStore.isSuperAdmin();

  return (
    <>
      <div onClick={_handleAddToRequisitionButton} className={opportunities.length === 0 ? "hidden" : "cursor-pointer"}>
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isLoading && _handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center">
              Edit {product.mpn} from Opportunity {opportunityDetail.bidNumber}
            </DialogTitle>
          </DialogHeader>
          <div hidden={isLoading} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="priceType">Price Type</Label>
              <Select value={chosenPriceType} onValueChange={handlePriceTypeChange}>
                <SelectTrigger id="priceType" className="w-full">
                  <SelectValue placeholder="Select a price type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ItemRebate">Rebate Amount</SelectItem>
                  {isSuperAdmin && <SelectItem value="ItemCost">Item Cost</SelectItem>}
                  {isSuperAdmin && <SelectItem value="ItemCostGP">Item Cost GP</SelectItem>}
                  {isSuperAdmin && <SelectItem value="Fixed">Fixed Pricing</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Amount</Label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-400">$</span>
                <Input
                  id="price"
                  type="number"
                  placeholder="Edit amount"
                  value={chosenBidPrice ?? ""}
                  onChange={handleBidPriceChange}
                  autoComplete="off"
                  className="pl-7"
                />
              </div>
            </div>

            {!isSuperAdmin &&
              <Alert variant="warning" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Access required</AlertTitle>
                <AlertDescription>
                  You need to be a Super Admin to edit opportunity (for the moment)
                </AlertDescription>
              </Alert>
            }
          </div>
          <div hidden={!isLoading} className="flex flex-col items-center space-y-2">
            <p>Please wait...</p>
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
          <DialogFooter className="flex justify-between" hidden={isLoading}>
            <Button variant="outline" onClick={_handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmProductOpportunityRequest}
              disabled={!isSuperAdmin || !chosenBidPrice || chosenBidPrice === 0 || chosenPriceType === "" || isLoading}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WithManufacturer(inject("store")(observer(EditProductFromOpportunity)));
